import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  HeaderContainer,
  HeaderRow,
  LeftControlGroup,
  RightControlGroup,
  TemplateButton,
  CopyButton,
  WeekTitle,
  StatusFilterBox,
  StatusFilterBoxWithBorder,
  StatusIndicator,
  StatusLabel,
  StatusCount,
  GuideButton,
} from './GroupLessonStyled';
import {
  TodayButton,
  NavButton,
  FilterButton,
  SettingsButton,
  ZoomButtonGroup,
  ZoomButton,
  ZoomLabel,
} from '../LessonCalendarStyled';

// 지점 스케줄 헤더의 '스케줄 설정'(hdr-compact)과 같은 아웃라인 소형 버튼 — 글자는 필터 버튼과 같은 청록·굵기
const SETTINGS_BUTTON_SX = {
  height: '30px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '0 12px',
  minWidth: 0,
  background: '#fff',
  color: '#4AB3BC',
  border: '1px solid #9FD3D7',
  borderRadius: '6px',
  '&:hover': { background: 'rgba(74, 179, 188, 0.08)', color: '#4AB3BC' },
  '& .MuiButton-startIcon': { mr: '4px' },
};

// 2행은 상태 필터를 날짜처럼 가로 정중앙(absolute)에 두는데, 폭이 좁으면 좌·우 버튼을 덮어 클릭이 막힌다.
// 좌(약 330px)·우(약 340px)와 중앙(약 260px)의 절반이 각 절반에 안 들어가는 폭(약 980px 이하)에서는 흐름 안으로 넣고 줄바꿈한다.
// 1행도 같은 이유로 오른쪽 버튼 묶음이 '오늘'(중앙+130px)과 겹치는 폭(약 860px 이하)에서는 다음 줄 오른쪽으로 내린다
const HEADER_CONTAINER_SX = {
  containerType: 'inline-size',
  containerName: 'grHeader',
  '@container grHeader (max-width: 1150px)': {
    '.hdr-zoom button': { minWidth: '26px', width: '26px', height: '26px', fontSize: '17px' },
    '.hdr-zoom p': { fontSize: '14px', minWidth: '30px' },
  },
  '@container grHeader (max-width: 980px)': {
    '.gr-row2': { flexWrap: 'wrap', columnGap: '8px', rowGap: '6px' },
    '.gr-status': { position: 'static', transform: 'none', margin: '0 auto' },
  },
  '@container grHeader (max-width: 860px)': {
    '.gr-row1': { flexWrap: 'wrap', rowGap: '6px' },
    '.gr-row1-right': { flexBasis: '100%', justifyContent: 'flex-end' },
    // 줄바꿈돼 행이 높아져도 날짜·오늘은 첫 줄에 둔다
    '.gr-nav, .gr-today': { top: 0, height: '30px' },
  },
};

const GroupLessonHeader = ({
  // 그룹 스케줄 설정
  onOpenSettings,
  // 날짜 네비게이션
  goToToday,
  goToPrevWeek,
  goToNextWeek,
  weekTitle,
  // 강의실 필터(다중) — 임직원 필터와 같은 모달을 연다
  selectedRoomCount = 0,
  onRoomFilterClick,
  // 임직원 필터(다중) — 지점 스케줄과 같은 임직원 필터 모달을 연다
  selectedTrainerCount = 0,
  onTrainerFilterClick,
  // 주간 복사
  onCopyWeekly,
  isEventsEmpty,
  isCopyLoading,
  // 예약 상태 필터
  selectedStatusFilter,
  onStatusFilterChange,
  reservationDeadLine,
  outOfReservation,
  noReservation,
  // 줌 컨트롤
  cellHeight,
  onZoomIn,
  onZoomOut,
  // 템플릿 관리
  onOpenTemplateManage,
  // 일괄 수정
  onOpenBulkEdit,
  // 다회원 일괄예약
  onOpenBatchReserve,
  canBatchReserve = true, // 일괄예약 = 예약을 만드는 동작이라 참석관리와 같은 권한으로 막는다
  // 가이드
  onOpenGuide,
}) => {
  const { t } = useTranslation();

  const STATUS_COLORS = {
    deadline: { main: '#55C982', active: '#3da86a' },
    under: { main: '#FFB659', active: '#d99a3a' },
    none: { main: '#EE753A', active: '#d15a25' },
  };

  return (
    <HeaderContainer sx={HEADER_CONTAINER_SX}>
      {/* 1행: 설정(좌) | 날짜(절대중앙) | 오늘(중앙 오른쪽) | 그룹수업 관리·가이드(우) */}
      <HeaderRow className="gr-row1">
        <LeftControlGroup>
          {/* 그룹 스케줄 설정 — 지점 스케줄처럼 1행 왼쪽 끝(아래 행 확대 비율 바로 위) */}
          <SettingsButton
            onClick={onOpenSettings}
            startIcon={<SettingsOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            sx={SETTINGS_BUTTON_SX}>
            {t('스케줄 설정')}
          </SettingsButton>
        </LeftControlGroup>

        <LeftControlGroup className="gr-nav" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
          <NavButton onClick={goToPrevWeek}>
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.5292 12.4701C10.6701 12.6109 10.7492 12.802 10.7492 13.0013C10.7492 13.2006 10.6701 13.3917 10.5292 13.5326C10.3883 13.6734 10.1972 13.7526 9.99794 13.7526C9.79868 13.7526 9.60758 13.6734 9.46669 13.5326L4.46668 8.53255C4.39676 8.46287 4.34129 8.38008 4.30343 8.28891C4.26558 8.19775 4.24609 8.10001 4.24609 8.0013C4.24609 7.90259 4.26558 7.80485 4.30343 7.71369C4.34129 7.62252 4.39676 7.53973 4.46668 7.47005L9.46669 2.47005C9.60758 2.32915 9.79868 2.25 9.99794 2.25C10.1972 2.25 10.3883 2.32915 10.5292 2.47005C10.6701 2.61095 10.7492 2.80204 10.7492 3.0013C10.7492 3.20056 10.6701 3.39165 10.5292 3.53255L6.06106 8.00068L10.5292 12.4701Z" fill="#A6A6A6" />
            </svg>
          </NavButton>
          <WeekTitle>{weekTitle}</WeekTitle>
          <NavButton onClick={goToNextWeek}>
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.47081 3.52995C5.32992 3.38905 5.25077 3.19796 5.25077 2.9987C5.25077 2.79944 5.32992 2.60835 5.47081 2.46745C5.61171 2.32655 5.80281 2.2474 6.00206 2.2474C6.20132 2.2474 6.39242 2.32655 6.53331 2.46745L11.5333 7.46745C11.6032 7.53713 11.6587 7.61992 11.6966 7.71109C11.7344 7.80225 11.7539 7.89999 11.7539 7.9987C11.7539 8.09741 11.7344 8.19515 11.6966 8.28631C11.6587 8.37748 11.6032 8.46027 11.5333 8.52995L6.53331 13.5299C6.39242 13.6709 6.20132 13.75 6.00206 13.75C5.80281 13.75 5.61171 13.6709 5.47081 13.5299C5.32992 13.3891 5.25077 13.198 5.25077 12.9987C5.25077 12.7994 5.32992 12.6083 5.47081 12.4674L9.93894 7.99932L5.47081 3.52995Z" fill="#A6A6A6" />
            </svg>
          </NavButton>
        </LeftControlGroup>

        <Box className="gr-today" sx={{ position: 'absolute', left: 'calc(50% + 130px)', display: 'flex', gap: '4px', alignItems: 'center' }}>
          <TodayButton onClick={goToToday} sx={{ ...SETTINGS_BUTTON_SX, height: '28px', fontSize: '12px', padding: '0 10px' }}>
            오늘
          </TodayButton>
        </Box>

        {/* 1행 오른쪽 끝 — 그룹수업 관리·가이드. 가이드는 디자인 그대로, 크기만 1행 버튼 수준으로 줄였다 */}
        <RightControlGroup className="gr-row1-right" sx={{ marginLeft: 'auto' }}>
          <TemplateButton onClick={onOpenTemplateManage}>그룹수업 관리</TemplateButton>
          <GuideButton onClick={onOpenGuide} sx={{ height: '30px', fontSize: '12px', padding: '0 12px' }}>
            📖 그룹수업 가이드
          </GuideButton>
        </RightControlGroup>
      </HeaderRow>

      {/* 2행: 줌+강의실·임직원 필터(좌) | 상태필터(절대중앙) | 일괄수정·일괄예약·일정복사(우) */}
      <HeaderRow className="gr-row2">
        <LeftControlGroup>
          {/* 확대 비율 — 지점 스케줄 헤더와 같은 컨트롤(좁은 폭에서 줄어드는 규칙도 같다) */}
          <ZoomButtonGroup className="hdr-zoom">
            <ZoomButton isDisabled={cellHeight <= 40} onClick={onZoomOut} disabled={cellHeight <= 40}>
              −
            </ZoomButton>
            <ZoomLabel>{Math.round((cellHeight / 80) * 100)}%</ZoomLabel>
            <ZoomButton isDisabled={cellHeight >= 120} onClick={onZoomIn} disabled={cellHeight >= 120}>
              +
            </ZoomButton>
          </ZoomButtonGroup>
          <FilterButton
            isActive={selectedRoomCount > 0}
            onClick={onRoomFilterClick}
            sx={{ fontSize: '14px', padding: '0 10px', minWidth: 0, whiteSpace: 'nowrap' }}>
            {t('강의실 필터')} {selectedRoomCount > 0 && `(${selectedRoomCount})`}
          </FilterButton>
          <FilterButton
            isActive={selectedTrainerCount > 0}
            onClick={onTrainerFilterClick}
            sx={{ fontSize: '14px', padding: '0 10px', minWidth: 0, whiteSpace: 'nowrap' }}>
            {t('임직원 필터')} {selectedTrainerCount > 0 && `(${selectedTrainerCount})`}
          </FilterButton>
        </LeftControlGroup>

        <LeftControlGroup className="gr-status" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
          <StatusFilterBoxWithBorder
            isActive={selectedStatusFilter === 'deadline'}
            statusColor={STATUS_COLORS.deadline.main}
            onClick={() => onStatusFilterChange(selectedStatusFilter === 'deadline' ? null : 'deadline')}>
            <StatusIndicator statusColor={STATUS_COLORS.deadline.main} isActive={selectedStatusFilter === 'deadline'} />
            <StatusLabel activeColor={STATUS_COLORS.deadline.active} isActive={selectedStatusFilter === 'deadline'}>
              예약마감
            </StatusLabel>
            <StatusCount activeColor={STATUS_COLORS.deadline.active} isActive={selectedStatusFilter === 'deadline'}>
              {reservationDeadLine || 0}
            </StatusCount>
          </StatusFilterBoxWithBorder>

          <StatusFilterBoxWithBorder
            isActive={selectedStatusFilter === 'under'}
            statusColor={STATUS_COLORS.under.main}
            onClick={() => onStatusFilterChange(selectedStatusFilter === 'under' ? null : 'under')}>
            <StatusIndicator statusColor={STATUS_COLORS.under.main} isActive={selectedStatusFilter === 'under'} />
            <StatusLabel activeColor={STATUS_COLORS.under.active} isActive={selectedStatusFilter === 'under'}>
              예약미달
            </StatusLabel>
            <StatusCount activeColor={STATUS_COLORS.under.active} isActive={selectedStatusFilter === 'under'}>
              {outOfReservation || 0}
            </StatusCount>
          </StatusFilterBoxWithBorder>

          <StatusFilterBox
            isActive={selectedStatusFilter === 'none'}
            statusColor={STATUS_COLORS.none.main}
            onClick={() => onStatusFilterChange(selectedStatusFilter === 'none' ? null : 'none')}>
            <StatusIndicator statusColor={STATUS_COLORS.none.main} isActive={selectedStatusFilter === 'none'} />
            <StatusLabel activeColor={STATUS_COLORS.none.active} isActive={selectedStatusFilter === 'none'}>
              예약없음
            </StatusLabel>
            <StatusCount activeColor={STATUS_COLORS.none.active} isActive={selectedStatusFilter === 'none'}>
              {noReservation || 0}
            </StatusCount>
          </StatusFilterBox>
        </LeftControlGroup>

        {/* 2행 오른쪽 — 일괄수정·일괄예약·일정복사 */}
        <RightControlGroup sx={{ marginLeft: 'auto' }}>
          <CopyButton size="large" onClick={onOpenBulkEdit}>
            <EditNoteOutlinedIcon sx={{ mr: '2px', width: '16px', height: '16px' }} />
            {t('스케줄 일괄수정')}
          </CopyButton>
          {canBatchReserve && (
            <CopyButton size="large" onClick={onOpenBatchReserve}>
              <GroupAddOutlinedIcon sx={{ mr: '2px', width: '16px', height: '16px' }} />
              {t('그룹수업 일괄예약')}
            </CopyButton>
          )}
          <CopyButton size="large" onClick={onCopyWeekly} disabled={isEventsEmpty || isCopyLoading}>
            {isCopyLoading ? t('복사 중...') : t('일정복사')}
            {!isCopyLoading && <ContentCopyOutlinedIcon sx={{ ml: '4px', width: '16px', height: '14px' }} />}
          </CopyButton>
        </RightControlGroup>
      </HeaderRow>
    </HeaderContainer>
  );
};

export default GroupLessonHeader;
